import React, { useState, useEffect } from 'react';
import {
  fetchLeaveBalances, createLeaveBalance, updateLeaveBalance,
  fetchLeaveRequests, createLeaveRequest, approveLeaveRequest, rejectLeaveRequest, cancelLeaveRequest,
  fetchStaffProfiles,
} from '../api';

const LEAVE_TYPES = [
  { value: 'ANNUAL', label: 'Annual Leave' },
  { value: 'SICK', label: 'Sick Leave' },
  { value: 'MATERNITY', label: 'Maternity Leave' },
  { value: 'PATERNITY', label: 'Paternity Leave' },
  { value: 'COMPASSIONATE', label: 'Compassionate Leave' },
  { value: 'STUDY', label: 'Study Leave' },
  { value: 'UNPAID', label: 'Unpaid Leave' },
];

const STATUS_COLORS = {
  PENDING: 'bg-yellow-200 text-yellow-800',
  APPROVED: 'bg-green-200 text-green-800',
  REJECTED: 'bg-red-200 text-red-800',
  CANCELLED: 'bg-gray-200 text-gray-800',
};

const CURRENT_YEAR = new Date().getFullYear();

const LeaveManagement = ({ authAxios, addToast }) => {
  const [tab, setTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ leave_type: 'ANNUAL', start_date: '', end_date: '', days_requested: '', reason: '' });
  const [rejectId, setRejectId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Leave Balance configuration state
  const [showBalanceForm, setShowBalanceForm] = useState(false);
  const [editingBalance, setEditingBalance] = useState(null);
  const [balanceForm, setBalanceForm] = useState({
    staff_profile: '', leave_type: 'ANNUAL', year: CURRENT_YEAR, entitlement: '', carried_over: '0',
  });
  const [savingBalance, setSavingBalance] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reqData, balData, staffData] = await Promise.all([
        fetchLeaveRequests(authAxios),
        fetchLeaveBalances(authAxios),
        fetchStaffProfiles(authAxios),
      ]);
      setRequests(reqData.results || reqData);
      setBalances(balData.results || balData);
      setStaffProfiles(staffData.results || staffData);
    } catch {
      addToast('Failed to load leave data', 'error');
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleSubmitRequest = async () => {
    if (!form.start_date || !form.end_date || !form.days_requested) {
      addToast('Please fill all required fields', 'error');
      return;
    }
    try {
      await createLeaveRequest(authAxios, form);
      addToast('Leave request submitted', 'success');
      setShowForm(false);
      setForm({ leave_type: 'ANNUAL', start_date: '', end_date: '', days_requested: '', reason: '' });
      loadData();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to submit request';
      addToast(msg, 'error');
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveLeaveRequest(authAxios, id);
      addToast('Leave approved', 'success');
      loadData();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || 'Approval failed';
      addToast(msg, 'error');
    }
  };

  const handleReject = async () => {
    try {
      await rejectLeaveRequest(authAxios, rejectId, { rejection_reason: rejectionReason });
      addToast('Leave rejected', 'success');
      setRejectId(null);
      setRejectionReason('');
      loadData();
    } catch {
      addToast('Rejection failed', 'error');
    }
  };

  const handleCancel = async (id) => {
    try {
      await cancelLeaveRequest(authAxios, id);
      addToast('Request cancelled', 'success');
      loadData();
    } catch {
      addToast('Cancel failed', 'error');
    }
  };

  // --- Leave Balance CRUD ---
  const openCreateBalance = () => {
    setEditingBalance(null);
    setBalanceForm({ staff_profile: '', leave_type: 'ANNUAL', year: CURRENT_YEAR, entitlement: '', carried_over: '0' });
    setShowBalanceForm(true);
  };

  const openEditBalance = (b) => {
    setEditingBalance(b);
    setBalanceForm({
      staff_profile: b.staff_profile,
      leave_type: b.leave_type,
      year: b.year,
      entitlement: b.entitlement,
      carried_over: b.carried_over,
    });
    setShowBalanceForm(true);
  };

  const handleSaveBalance = async () => {
    if (!balanceForm.staff_profile || !balanceForm.entitlement) {
      addToast('Employee and Entitlement are required', 'error');
      return;
    }
    setSavingBalance(true);
    try {
      if (editingBalance) {
        await updateLeaveBalance(authAxios, editingBalance.id, {
          entitlement: balanceForm.entitlement,
          carried_over: balanceForm.carried_over,
        });
        addToast('Leave balance updated', 'success');
      } else {
        await createLeaveBalance(authAxios, balanceForm);
        addToast('Leave balance created', 'success');
      }
      setShowBalanceForm(false);
      setEditingBalance(null);
      loadData();
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.response?.data && typeof err.response.data === 'object'
          ? Object.values(err.response.data).flat().join(', ')
          : 'Failed to save balance');
      addToast(msg, 'error');
    } finally {
      setSavingBalance(false);
    }
  };

  const getStaffName = (profileId) => {
    const p = staffProfiles.find(s => s.id === profileId);
    if (!p) return `Staff #${profileId}`;
    return `${p.user_first_name || ''} ${p.user_last_name || ''}`.trim() || p.username || `Staff #${profileId}`;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Leave Management</h2>
        <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          New Request
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-4">
        <button
          onClick={() => setTab('requests')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'requests' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}
        >
          Requests
        </button>
        <button
          onClick={() => setTab('balances')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'balances' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}
        >
          Balances
        </button>
      </div>

      {/* New Request Form */}
      {showForm && (
        <div className="bg-gray-50 border rounded p-4 mb-4">
          <h3 className="font-semibold mb-3">New Leave Request</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-600">Leave Type</label>
              <select className="border rounded p-2 w-full text-sm" value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })}>
                {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600">Start Date</label>
              <input type="date" className="border rounded p-2 w-full text-sm" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-600">End Date</label>
              <input type="date" className="border rounded p-2 w-full text-sm" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Days</label>
              <input type="number" className="border rounded p-2 w-full text-sm" value={form.days_requested} onChange={e => setForm({ ...form, days_requested: e.target.value })} />
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-xs text-gray-600">Reason</label>
            <textarea className="border rounded p-2 w-full text-sm" rows="2" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} />
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={handleSubmitRequest} className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700">Submit</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-gray-200 rounded text-sm hover:bg-gray-300">Cancel</button>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectId && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="font-semibold mb-3">Reject Leave Request</h3>
            <textarea
              className="border rounded p-2 w-full text-sm"
              rows="3"
              placeholder="Reason for rejection..."
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
            />
            <div className="mt-3 flex gap-2 justify-end">
              <button onClick={() => { setRejectId(null); setRejectionReason(''); }} className="px-4 py-2 bg-gray-200 rounded text-sm">Cancel</button>
              <button onClick={handleReject} className="px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700">Reject</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p className="text-center py-8">Loading...</p> : tab === 'requests' ? (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b p-2 text-left">Employee</th>
              <th className="border-b p-2 text-left">Type</th>
              <th className="border-b p-2 text-left">Dates</th>
              <th className="border-b p-2 text-right">Days</th>
              <th className="border-b p-2 text-left">Status</th>
              <th className="border-b p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map(r => (
              <tr key={r.id} className="border-b hover:bg-gray-50">
                <td className="p-2">{r.staff_username || `Staff #${r.staff_profile}`}</td>
                <td className="p-2">{LEAVE_TYPES.find(t => t.value === r.leave_type)?.label || r.leave_type}</td>
                <td className="p-2">{r.start_date} — {r.end_date}</td>
                <td className="p-2 text-right">{r.days_requested}</td>
                <td className="p-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[r.status] || ''}`}>{r.status_display || r.status}</span>
                </td>
                <td className="p-2 space-x-1">
                  {r.status === 'PENDING' && (
                    <>
                      <button onClick={() => handleApprove(r.id)} className="px-2 py-1 bg-green-500 text-white rounded text-xs">Approve</button>
                      <button onClick={() => setRejectId(r.id)} className="px-2 py-1 bg-red-500 text-white rounded text-xs">Reject</button>
                    </>
                  )}
                  {['PENDING', 'APPROVED'].includes(r.status) && (
                    <button onClick={() => handleCancel(r.id)} className="px-2 py-1 bg-gray-500 text-white rounded text-xs">Cancel</button>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && <tr><td colSpan="6" className="p-4 text-center text-gray-500">No leave requests</td></tr>}
          </tbody>
        </table>
      ) : (
        <div>
          {/* Balance configuration form modal */}
          {showBalanceForm && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
                <h3 className="font-semibold mb-4 text-lg">{editingBalance ? 'Edit Leave Balance' : 'Configure Leave Balance'}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Employee</label>
                    <select
                      className="border rounded p-2 w-full text-sm"
                      value={balanceForm.staff_profile}
                      onChange={e => setBalanceForm({ ...balanceForm, staff_profile: Number(e.target.value) })}
                      disabled={!!editingBalance}
                    >
                      <option value="">Select employee...</option>
                      {staffProfiles.map(sp => (
                        <option key={sp.id} value={sp.id}>
                          {`${sp.user_first_name || ''} ${sp.user_last_name || ''}`.trim() || sp.username || `Staff #${sp.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Leave Type</label>
                      <select
                        className="border rounded p-2 w-full text-sm"
                        value={balanceForm.leave_type}
                        onChange={e => setBalanceForm({ ...balanceForm, leave_type: e.target.value })}
                        disabled={!!editingBalance}
                      >
                        {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Year</label>
                      <input
                        type="number"
                        className="border rounded p-2 w-full text-sm"
                        value={balanceForm.year}
                        onChange={e => setBalanceForm({ ...balanceForm, year: Number(e.target.value) })}
                        disabled={!!editingBalance}
                        min={CURRENT_YEAR - 1}
                        max={CURRENT_YEAR + 1}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Entitlement (days)</label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        className="border rounded p-2 w-full text-sm"
                        value={balanceForm.entitlement}
                        onChange={e => setBalanceForm({ ...balanceForm, entitlement: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Carried Over (days)</label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        className="border rounded p-2 w-full text-sm"
                        value={balanceForm.carried_over}
                        onChange={e => setBalanceForm({ ...balanceForm, carried_over: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2 justify-end">
                  <button onClick={() => { setShowBalanceForm(false); setEditingBalance(null); }} className="px-4 py-2 bg-gray-200 rounded text-sm hover:bg-gray-300">Cancel</button>
                  <button onClick={handleSaveBalance} disabled={savingBalance} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
                    {savingBalance ? 'Saving...' : editingBalance ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end mb-3">
            <button onClick={openCreateBalance} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              Configure Balance
            </button>
          </div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border-b p-2 text-left">Employee</th>
                <th className="border-b p-2 text-left">Leave Type</th>
                <th className="border-b p-2 text-right">Year</th>
                <th className="border-b p-2 text-right">Entitlement</th>
                <th className="border-b p-2 text-right">Used</th>
                <th className="border-b p-2 text-right">Carried Over</th>
                <th className="border-b p-2 text-right">Available</th>
                <th className="border-b p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {balances.map(b => (
                <tr key={b.id} className="border-b hover:bg-gray-50">
                  <td className="p-2">{b.staff_username || getStaffName(b.staff_profile)}</td>
                  <td className="p-2">{LEAVE_TYPES.find(t => t.value === b.leave_type)?.label || b.leave_type}</td>
                  <td className="p-2 text-right">{b.year}</td>
                  <td className="p-2 text-right">{b.entitlement}</td>
                  <td className="p-2 text-right">{b.used}</td>
                  <td className="p-2 text-right">{b.carried_over}</td>
                  <td className="p-2 text-right font-semibold">{b.available}</td>
                  <td className="p-2 text-center">
                    <button onClick={() => openEditBalance(b)} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">Edit</button>
                  </td>
                </tr>
              ))}
              {balances.length === 0 && <tr><td colSpan="8" className="p-4 text-center text-gray-500">No leave balances configured</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
